import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Search,
  AlertTriangle,
  CheckCircle,
  HelpCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Copy,
  Check,
  Sparkles,
  Shield,
  AlertCircle,
  MessageSquare,
  Hash,
  Target,
  Zap,
  Twitter,
  Linkedin
} from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';

// Quick analysis types (6 categories)
type SimplifiedCategory = 'hedging' | 'missingSources' | 'suspiciousNumbers' | 'filler' | 'confidenceTricks' | 'potentialErrors';

interface Finding {
  text: string;
  start: number;
  end: number;
  explanation: string;
}

interface CategoryResult {
  found: boolean;
  count: number;
  severity: 'low' | 'medium' | 'high';
  findings: Finding[];
}

interface QuickAnalysisResult {
  id: string;
  trustScore: number;
  scoreLabel: string;
  scoreColor: string;
  categories: Record<SimplifiedCategory, CategoryResult>;
  topIssues: { category: SimplifiedCategory; count: number; label: string }[];
  textLength: number;
  cached?: boolean;
}

// Deep analysis types (12 categories)
interface DeepPassage {
  text: string;
  explanation: string;
}

interface DeepCategory {
  found: boolean;
  severity: 'low' | 'medium' | 'high';
  passages: DeepPassage[];
}

interface DeepAnalysisResult {
  summary: string;
  overall_assessment: string;
  categories: {
    confident_claim: DeepCategory;
    hedging: DeepCategory;
    false_precision: DeepCategory;
    unsupported: DeepCategory;
    vague_attribution: DeepCategory;
    circular_reasoning: DeepCategory;
    filler: DeepCategory;
    disclaimer: DeepCategory;
    people_pleasing: DeepCategory;
    possible_hallucination: DeepCategory;
    outdated_info: DeepCategory;
    oversimplification: DeepCategory;
  };
  recommendations: string[];
}

// Category display info
const CATEGORY_INFO: Record<SimplifiedCategory, { label: string; icon: typeof AlertTriangle; color: string; description: string }> = {
  hedging: {
    label: 'Hedging Language',
    icon: MessageSquare,
    color: 'yellow',
    description: '"May", "might", "possibly" - reduces commitment'
  },
  missingSources: {
    label: 'Missing Sources',
    icon: HelpCircle,
    color: 'orange',
    description: '"Studies show" without citing specific studies'
  },
  suspiciousNumbers: {
    label: 'Suspicious Numbers',
    icon: Hash,
    color: 'red',
    description: 'Oddly precise figures without citations'
  },
  filler: {
    label: 'Filler Content',
    icon: Zap,
    color: 'slate',
    description: '"Great question!" - adds no information'
  },
  confidenceTricks: {
    label: 'Confidence Tricks',
    icon: Target,
    color: 'purple',
    description: '"Obviously", "clearly" - asserts without evidence'
  },
  potentialErrors: {
    label: 'Potential Errors',
    icon: AlertCircle,
    color: 'red',
    description: 'Absolutes, outdated info, oversimplifications'
  }
};

const DEEP_CATEGORY_LABELS: Record<string, string> = {
  confident_claim: 'Confident Claims',
  hedging: 'Hedging',
  false_precision: 'False Precision',
  unsupported: 'Unsupported',
  vague_attribution: 'Vague Attribution',
  circular_reasoning: 'Circular Reasoning',
  filler: 'Filler Content',
  disclaimer: 'Disclaimers',
  people_pleasing: 'People Pleasing',
  possible_hallucination: 'Possible Hallucination',
  outdated_info: 'Outdated Info',
  oversimplification: 'Oversimplification'
};

const SEVERITY_COLORS = {
  low: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  medium: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  high: 'bg-red-500/20 text-red-400 border-red-500/30'
};

export default function SpotTheFlaw() {
  const [searchParams] = useSearchParams();
  const { fetchApi } = useApi();
  const { user } = useAuth();

  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [deepLoading, setDeepLoading] = useState(false);
  const [quickResult, setQuickResult] = useState<QuickAnalysisResult | null>(null);
  const [deepResult, setDeepResult] = useState<DeepAnalysisResult | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeepView, setShowDeepView] = useState(false);

  // Load shared result on mount if id param exists
  const shareId = searchParams.get('id');

  useEffect(() => {
    if (shareId) {
      loadSharedResult(shareId);
    }
  }, [shareId]);

  const loadSharedResult = async (id: string) => {
    setLoading(true);
    try {
      const data = await fetchApi<QuickAnalysisResult & { deepResults?: DeepAnalysisResult }>(`/api/spot-the-flaw/result/${id}`);
      setQuickResult(data);
      if (data.deepResults) {
        setDeepResult(data.deepResults as DeepAnalysisResult);
      }
    } catch (err) {
      setError('Analysis not found');
    } finally {
      setLoading(false);
    }
  };

  const analyzeText = async () => {
    if (text.length < 50) {
      setError('Please enter at least 50 characters');
      return;
    }

    setLoading(true);
    setError(null);
    setQuickResult(null);
    setDeepResult(null);
    setShowDeepView(false);

    try {
      const data = await fetchApi<QuickAnalysisResult>('/api/spot-the-flaw/analyze', {
        method: 'POST',
        body: JSON.stringify({ text })
      });
      setQuickResult(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Analysis failed';
      if (message.includes('429') || message.includes('limit')) {
        setError('Daily limit reached. Sign up for more analyses.');
      } else {
        setError('Analysis failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const runDeepAnalysis = async () => {
    if (!quickResult) return;

    setDeepLoading(true);

    try {
      const data = await fetchApi<{ success: boolean; deepResults: DeepAnalysisResult }>('/api/spot-the-flaw/deep-analyze', {
        method: 'POST',
        body: JSON.stringify({ text, analysisId: quickResult.id })
      });
      setDeepResult(data.deepResults);
      setShowDeepView(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Deep analysis failed';
      if (message.includes('429') || message.includes('limit')) {
        setError('Daily deep analysis limit reached. Sign up for more.');
      } else {
        setError('Deep analysis failed. Please try again.');
      }
    } finally {
      setDeepLoading(false);
    }
  };

  const copyShareLink = () => {
    if (quickResult) {
      navigator.clipboard.writeText(`${window.location.origin}/spot-the-flaw?id=${quickResult.id}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareOnTwitter = () => {
    if (!quickResult) return;
    const shareUrl = `${window.location.origin}/spot-the-flaw?id=${quickResult.id}`;
    const label = quickResult.scoreLabel.replace(/"/g, '');
    const text = `I analyzed some AI text with Spot the Flaw - Trust Score: ${quickResult.trustScore}/100 (${label}). Try it yourself!`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank', 'noopener,noreferrer');
  };

  const shareOnLinkedIn = () => {
    if (!quickResult) return;
    const shareUrl = `${window.location.origin}/spot-the-flaw?id=${quickResult.id}`;
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(linkedinUrl, '_blank', 'noopener,noreferrer');
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-blue-400';
    if (score >= 70) return 'text-emerald-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 90) return 'from-blue-600/30 to-blue-500/10 border-blue-500/40';
    if (score >= 70) return 'from-emerald-600/30 to-emerald-500/10 border-emerald-500/40';
    if (score >= 40) return 'from-yellow-600/30 to-yellow-500/10 border-yellow-500/40';
    return 'from-red-600/30 to-red-500/10 border-red-500/40';
  };

  // Input view
  if (!quickResult) {
    return (
      <div className="min-h-screen bg-slate-900 py-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-amber-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Spot the Flaw
            </h1>
            <p className="text-slate-400 text-lg max-w-xl mx-auto">
              Get an instant Trust Score for any AI-generated text. Find hedging, missing sources, suspicious numbers, and more.
            </p>
          </div>

          {/* Text input */}
          <div className="bg-slate-800 rounded-xl p-6">
            <label className="block text-white font-medium mb-2">
              Paste AI-generated text here
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste a ChatGPT response, Claude answer, or any AI-generated content..."
              className="w-full h-64 bg-slate-900 border border-slate-700 rounded-lg p-4 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
            />
            <div className="flex justify-between items-center mt-3">
              <span className={`text-sm ${text.length < 50 ? 'text-slate-500' : text.length > 5000 ? 'text-red-400' : 'text-slate-400'}`}>
                {text.length}/5000 characters {text.length < 50 && '(minimum 50)'}
              </span>
              <button
                onClick={analyzeText}
                disabled={loading || text.length < 50 || text.length > 5000}
                className="bg-amber-600 hover:bg-amber-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    Analyze Text
                  </>
                )}
              </button>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}
          </div>

          {/* What we check for */}
          <div className="mt-8 bg-slate-800/50 rounded-xl p-6">
            <h3 className="text-white font-semibold mb-4">What we check for:</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(CATEGORY_INFO).map(([key, info]) => (
                <div key={key} className="text-center">
                  <div className={`w-10 h-10 bg-${info.color}-500/20 rounded-full flex items-center justify-center mx-auto mb-2`}>
                    <info.icon className={`w-5 h-5 text-${info.color}-400`} />
                  </div>
                  <div className="text-white text-sm font-medium">{info.label}</div>
                  <div className="text-slate-500 text-xs">{info.description.split(' - ')[1]}</div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 text-center">
            <p className="text-slate-400 text-sm">
              Want to develop these skills yourself?{' '}
              <Link to="/courses/close-reading" className="text-amber-400 hover:text-amber-300">
                Take our Close Reading course →
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Results view
  return (
    <div className="min-h-screen bg-slate-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Trust Score Header */}
        <div className={`bg-gradient-to-br ${getScoreBgColor(quickResult.trustScore)} border rounded-xl p-6 mb-6`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-white mb-1">Trust Score</h2>
              <p className="text-slate-300 text-sm">
                {quickResult.cached ? 'Previously analyzed • ' : ''}
                {quickResult.textLength.toLocaleString()} characters analyzed
              </p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className={`text-5xl font-bold ${getScoreColor(quickResult.trustScore)}`}>
                  {quickResult.trustScore}
                </div>
                <div className="text-slate-400 text-sm mt-1">
                  {quickResult.scoreLabel}
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={copyShareLink}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg transition-colors text-sm"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
            <button
              onClick={shareOnTwitter}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg transition-colors text-sm"
              title="Share on X/Twitter"
            >
              <Twitter className="w-4 h-4" />
              <span className="hidden sm:inline">Tweet</span>
            </button>
            <button
              onClick={shareOnLinkedIn}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg transition-colors text-sm"
              title="Share on LinkedIn"
            >
              <Linkedin className="w-4 h-4" />
              <span className="hidden sm:inline">Share</span>
            </button>
            <button
              onClick={() => {
                setQuickResult(null);
                setDeepResult(null);
                setText('');
              }}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg transition-colors text-sm"
            >
              <Search className="w-4 h-4" />
              Analyze Another
            </button>
            {!deepResult && (
              <button
                onClick={runDeepAnalysis}
                disabled={deepLoading}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-600 text-white rounded-lg transition-colors text-sm ml-auto"
              >
                {deepLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Running Deep Analysis...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    See Deep Analysis
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* View toggle if deep results exist */}
        {deepResult && (
          <div className="flex bg-slate-800 rounded-lg p-1 mb-6">
            <button
              onClick={() => setShowDeepView(false)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                !showDeepView ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              Quick Analysis (6 categories)
            </button>
            <button
              onClick={() => setShowDeepView(true)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                showDeepView ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              Deep Analysis (12 categories)
            </button>
          </div>
        )}

        {/* Quick Analysis View */}
        {!showDeepView && (
          <>
            {/* Top Issues Summary */}
            {quickResult.topIssues.length > 0 && (
              <div className="bg-slate-800 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-2 text-slate-400 text-sm mb-3">
                  <AlertTriangle className="w-4 h-4" />
                  Top Issues Found
                </div>
                <div className="flex flex-wrap gap-2">
                  {quickResult.topIssues.map((issue) => (
                    <span
                      key={issue.category}
                      className="px-3 py-1 bg-slate-700 text-white rounded-full text-sm"
                    >
                      {issue.label} ({issue.count})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Category Breakdown */}
            <div className="bg-slate-800 rounded-xl p-6 mb-6">
              <h3 className="text-white font-semibold mb-4">Category Breakdown</h3>
              <div className="space-y-3">
                {Object.entries(quickResult.categories).map(([key, category]) => {
                  const catKey = key as SimplifiedCategory;
                  const info = CATEGORY_INFO[catKey];
                  const isExpanded = expandedCategory === key;

                  if (!category.found) {
                    return (
                      <div key={key} className="flex items-center gap-3 px-4 py-3 bg-slate-700/30 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                        <span className="text-slate-400">{info.label}</span>
                        <span className="text-emerald-400 text-sm ml-auto">No issues</span>
                      </div>
                    );
                  }

                  return (
                    <div key={key} className={`border rounded-lg overflow-hidden ${SEVERITY_COLORS[category.severity]}`}>
                      <button
                        onClick={() => setExpandedCategory(isExpanded ? null : key)}
                        className="w-full px-4 py-3 flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-3">
                          <info.icon className="w-5 h-5" />
                          <div>
                            <div className="font-medium">{info.label}</div>
                            <div className="text-sm opacity-75">{info.description}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium">
                            {category.count} found
                          </span>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </div>
                      </button>

                      {isExpanded && category.findings.length > 0 && (
                        <div className="px-4 pb-4 border-t border-current/20 space-y-2">
                          {category.findings.map((finding, idx) => (
                            <div key={idx} className="mt-3 pl-4 border-l-2 border-current/30">
                              <div className="text-sm font-mono opacity-90">"{finding.text}"</div>
                              <div className="text-xs opacity-75 mt-1">{finding.explanation}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Deep Analysis View */}
        {showDeepView && deepResult && (
          <>
            {/* Summary */}
            <div className="bg-slate-800 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-amber-400 mt-1" />
                <div>
                  <h3 className="text-white font-semibold mb-2">AI Analysis Summary</h3>
                  <p className="text-slate-300">{deepResult.summary}</p>
                  <p className="text-slate-400 mt-2">{deepResult.overall_assessment}</p>
                </div>
              </div>
            </div>

            {/* 12-Category Breakdown */}
            <div className="bg-slate-800 rounded-xl p-6 mb-6">
              <h3 className="text-white font-semibold mb-4">Detailed Categories</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(deepResult.categories).map(([key, category]) => {
                  const isExpanded = expandedCategory === `deep-${key}`;
                  const label = DEEP_CATEGORY_LABELS[key] || key;

                  if (!category.found) {
                    return (
                      <div key={key} className="flex items-center gap-2 px-3 py-2 bg-slate-700/30 rounded-lg">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        <span className="text-slate-400 text-sm">{label}</span>
                      </div>
                    );
                  }

                  return (
                    <div key={key} className={`border rounded-lg ${SEVERITY_COLORS[category.severity]}`}>
                      <button
                        onClick={() => setExpandedCategory(isExpanded ? null : `deep-${key}`)}
                        className="w-full px-3 py-2 flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="font-medium text-sm">{label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs">{category.passages.length} issues</span>
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                      </button>
                      {isExpanded && category.passages.length > 0 && (
                        <div className="px-3 pb-3 border-t border-current/20">
                          {category.passages.map((passage, idx) => (
                            <div key={idx} className="mt-2 text-xs">
                              <div className="font-mono opacity-90">"{passage.text}"</div>
                              <div className="opacity-75 mt-1">{passage.explanation}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recommendations */}
            {deepResult.recommendations.length > 0 && (
              <div className="bg-slate-800 rounded-xl p-6 mb-6">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-400" />
                  Recommendations
                </h3>
                <ul className="space-y-2">
                  {deepResult.recommendations.map((rec, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-300">
                      <ExternalLink className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {/* Error display */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 mb-6">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Signup prompt for non-authenticated users */}
        {!user && (
          <div className="bg-gradient-to-r from-blue-600/20 to-indigo-600/20 border border-blue-500/30 rounded-xl p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-white font-semibold mb-1">
                  Save your results & get unlimited analyses
                </h3>
                <p className="text-slate-300 text-sm">
                  Create a free account to track your analyses over time and access deep AI-powered insights.
                </p>
              </div>
              <div className="flex gap-2">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold py-2 px-4 rounded-lg transition-colors text-sm"
                >
                  Sign Up Free
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors text-sm"
                >
                  Log In
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Relevant courses based on issues found */}
        {quickResult.topIssues.length > 0 && (
          <div className="bg-slate-800 rounded-xl p-6 mb-6">
            <h3 className="text-white font-semibold mb-4">Develop Your Skills</h3>
            <p className="text-slate-400 text-sm mb-4">
              Based on the issues found, these courses can help you spot similar patterns:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {quickResult.topIssues.some(i => i.category === 'missingSources' || i.category === 'suspiciousNumbers') && (
                <Link
                  to="/courses/close-reading"
                  className="flex items-center gap-3 p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors group"
                >
                  <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <Search className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <div className="text-white font-medium group-hover:text-amber-400 transition-colors">Close Reading</div>
                    <div className="text-slate-400 text-xs">Spot unsupported claims & verify sources</div>
                  </div>
                </Link>
              )}
              {quickResult.topIssues.some(i => i.category === 'hedging' || i.category === 'filler') && (
                <Link
                  to="/tracks/ai-literacy"
                  className="flex items-center gap-3 p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors group"
                >
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="text-white font-medium group-hover:text-blue-400 transition-colors">AI Literacy</div>
                    <div className="text-slate-400 text-xs">Understand AI language patterns</div>
                  </div>
                </Link>
              )}
              {quickResult.topIssues.some(i => i.category === 'confidenceTricks' || i.category === 'potentialErrors') && (
                <Link
                  to="/tracks/thinking-skills"
                  className="flex items-center gap-3 p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors group"
                >
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <Target className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <div className="text-white font-medium group-hover:text-purple-400 transition-colors">Thinking Skills</div>
                    <div className="text-slate-400 text-xs">Detect logical fallacies & errors</div>
                  </div>
                </Link>
              )}
              <Link
                to="/bingo"
                className="flex items-center gap-3 p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors group"
              >
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <div className="text-white font-medium group-hover:text-emerald-400 transition-colors">Bias Bingo</div>
                  <div className="text-slate-400 text-xs">Test your bias awareness</div>
                </div>
              </Link>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="bg-gradient-to-r from-amber-600/20 to-orange-600/20 border border-amber-500/30 rounded-xl p-6 text-center">
          <h3 className="text-white font-semibold mb-2">
            Want to master these skills?
          </h3>
          <p className="text-slate-300 mb-4">
            Learn to spot AI issues yourself with our free Close Reading course.
          </p>
          <Link
            to="/courses/close-reading"
            className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Start Free Course
            <ExternalLink className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
